import React, {useEffect, useRef, useState} from "react";
import {
    AccountingAccountList,
    AccountingAccountRestControllerApiFactory,
    AccountingPeriodRestControllerApiFactory,
    JournalEntry,
    JournalEntryRestControllerApiFactory,
    JournalLine,
    JournalSuggestionDto,
} from "arteco-api-client-ts";
import {fmtAmt, fmtAccountCode} from "../../../../components/utils/numbers";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {AddRounded, DeleteOutlineRounded} from "@mui/icons-material";
import {SoftChip, tableContainerSx, tableSx, Tone} from "../../../../components/kit";
import FormModel, {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager, axiosInstance} from "../../../../components/utils/api";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import {types} from "../../../../components/form/types";
import {postableAccounts, renderAccountOption} from "../../../../components/utils/accounts";
import {FormButtonProps} from "../../../../components/form/form_inputs/form_button";
import {useLocation, useNavigate} from "react-router-dom";

// ─── Line row state ───────────────────────────────────────────────────────────

interface LineRow {
    key: string;
    id?: number;
    account: AccountingAccountList | null;
    debit: string;
    credit: string;
    description: string;
}

function toLineRow(l: JournalLine): LineRow {
    return {
        key: String(l.id ?? Math.random()),
        id: l.id ?? undefined,
        account: (l.account as any) ?? null,
        debit: l.debit != null ? String(l.debit) : "",
        credit: l.credit != null ? String(l.credit) : "",
        description: l.description ?? "",
    };
}

function toJournalLine(row: LineRow, entryId: number): JournalLine {
    return {
        ...(row.id ? {id: row.id} : {}),
        account: row.account,
        debit: row.debit ? parseFloat(row.debit) : undefined,
        credit: row.credit ? parseFloat(row.credit) : undefined,
        description: row.description || undefined,
        journalEntry: {id: entryId} as any,
    } as unknown as JournalLine;
}

// ─── Line editor row ──────────────────────────────────────────────────────────

interface LineEditorProps {
    row: LineRow;
    disabled: boolean;
    onChange: (changes: Partial<LineRow>) => void;
    onDelete: () => void;
}

const LineEditor = ({row, disabled, onChange, onDelete}: LineEditorProps) => {
    const apiManager = useApiManager();
    const [options, setOptions] = useState<AccountingAccountList[]>(
        row.account ? [row.account] : []
    );
    const [inputValue, setInputValue] = useState(
        row.account ? `${fmtAccountCode(row.account.code)} — ${(row.account as any).name ?? ""}` : ""
    );
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!row.account) {
            const ctrl = apiManager.factory(AccountingAccountRestControllerApiFactory);
            apiManager.execute(
                ctrl.listAccountingAccount(undefined, undefined, undefined, undefined, undefined, true, undefined, false)
            ).then(list => setOptions(postableAccounts(list as any)));
        }
    }, []);

    const search = (term: string) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            const ctrl = apiManager.factory(AccountingAccountRestControllerApiFactory);
            const req = term.trim()
                ? ctrl.listAccountingAccount(undefined, undefined, undefined, undefined, undefined, undefined, term.trim(), false)
                : ctrl.listAccountingAccount(undefined, undefined, undefined, undefined, undefined, true, undefined, false);
            apiManager.execute(req).then(list => setOptions(postableAccounts(list as any)));
        }, 250);
    };

    return (
        <TableRow sx={{"&:hover": {bgcolor: "transparent"}}}>
            <TableCell sx={{p: 0.5}}>
                <Autocomplete
                    size="small"
                    disabled={disabled}
                    options={options}
                    value={row.account}
                    inputValue={inputValue}
                    filterOptions={x => x}
                    noOptionsText={inputValue.trim() ? "Sin resultados" : "Código, nombre o con punto: 572.2"}
                    onInputChange={(_, val) => { setInputValue(val); search(val); }}
                    onChange={(_, val) => {
                        onChange({account: val ?? null});
                        setInputValue(val ? `${fmtAccountCode(val.code)} — ${(val as any).name ?? ""}` : "");
                    }}
                    getOptionLabel={o => `${fmtAccountCode(o.code)} — ${(o as any).name ?? ""}`}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderOption={(props, option) => renderAccountOption(props, option as any)}
                    renderInput={params => (
                        <TextField {...params} placeholder="Cuenta…" variant="standard" size="small"/>
                    )}
                    sx={{width: "100%"}}
                />
            </TableCell>
            <TableCell sx={{p: 0.5}}>
                <TextField
                    size="small" variant="standard" placeholder="Descripción" disabled={disabled}
                    value={row.description}
                    onChange={e => onChange({description: e.target.value})}
                    fullWidth
                />
            </TableCell>
            <TableCell sx={{p: 0.5}} align="right">
                <TextField
                    size="small" variant="standard" type="number" placeholder="0.00" disabled={disabled}
                    value={row.debit}
                    onChange={e => {
                        const val = e.target.value;
                        onChange({debit: val, ...(val ? {credit: ""} : {})});
                    }}
                    inputProps={{style: {textAlign: "right"}, step: "0.01"}}
                    sx={{width: "100%"}}
                />
            </TableCell>
            <TableCell sx={{p: 0.5}} align="right">
                <TextField
                    size="small" variant="standard" type="number" placeholder="0.00" disabled={disabled}
                    value={row.credit}
                    onChange={e => {
                        const val = e.target.value;
                        onChange({credit: val, ...(val ? {debit: ""} : {})});
                    }}
                    inputProps={{style: {textAlign: "right"}, step: "0.01"}}
                    sx={{width: "100%"}}
                />
            </TableCell>
            <TableCell sx={{p: 0.5}}>
                {!disabled && (
                    <Tooltip title="Quitar la línea">
                        <IconButton size="small" color="error" onClick={onDelete}>
                            <DeleteOutlineRounded fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                )}
            </TableCell>
        </TableRow>
    );
};

// ─── Status chip ──────────────────────────────────────────────────────────────

const statusTone: Record<string, Tone> = {
    BORRADOR: "warning",
    CONFIRMADO: "success",
    ANULADO: "error",
};

const statusLabel: Record<string, string> = {
    BORRADOR: "Borrador",
    CONFIRMADO: "Confirmado",
    ANULADO: "Anulado",
};

// ─── Main form ────────────────────────────────────────────────────────────────

export const JournalEntryEditPage = () => {
    const apiManager = useApiManager();
    const navigate = useNavigate();
    const ctrl = apiManager.factory(JournalEntryRestControllerApiFactory);
    const periodCtrl = apiManager.factory(AccountingPeriodRestControllerApiFactory);
    const location = useLocation();
    const suggestion: JournalSuggestionDto | undefined = location.state?.suggestion;
    const suggestionAppliedRef = useRef(false);

    const [lineRows, setLineRows] = useState<LineRow[]>([]);
    const loadedBeanIdRef = useRef<number | undefined>(undefined);

    // ── Save unsaved lines; returns updated rows with server IDs ──────────────
    const saveAllLines = async (entryId: number): Promise<void> => {
        const toSave = lineRows.filter(
            r => r.account && (parseFloat(r.debit) > 0 || parseFloat(r.credit) > 0)
        );
        if (toSave.length === 0) return;
        const results = await Promise.all(
            toSave.map(async row => {
                const saved = await apiManager.execute(
                    ctrl.saveLineJournalEntry(entryId, toJournalLine(row, entryId))
                );
                return {key: row.key, id: (saved as any).id as number};
            })
        );
        setLineRows(prev =>
            prev.map(r => {
                const hit = results.find(res => res.key === r.key);
                return hit ? {...r, id: hit.id} : r;
            })
        );
    };

    const form: FormModel<JournalEntry> = useForm({
        fetcher: (id) => ctrl.getJournalEntry(id),
        updater: (bean) => {
            // Strip lines: managed separately via saveLineJournalEntry.
            const {lines: _lines, dismissed, ...header} = bean as any;
            // Convert empty string dismissed to undefined (backend expects null/absent).
            return ctrl.saveJournalEntry({
                ...header,
                dismissed: dismissed || undefined,
            } as JournalEntry);
        },
        remover: (id) => ctrl.deleteOrDismissJournalEntry(id),
        factory: () => ({
            entryDate: new Date().toISOString().split("T")[0],
            description: suggestion?.label ?? "",
            origin: "MANUAL",
            lines: [],
        } as unknown as JournalEntry),
        preUpdate: async (bean) => {
            if (bean.id) {
                try {
                    await saveAllLines(bean.id);
                } catch {
                    return "Error al guardar las líneas del asiento.";
                }
            }
            return true;
        },
        postLoad: (bean) => {
            if (!bean.id && !bean.period) {
                apiManager.execute(periodCtrl.currentAccountingPeriod())
                    .then(period => form.triggerField("period", period))
                    .catch(() => {});
            }
        },
        postUpdate: (bean) => {
            if (!form.getId() && bean.id) {
                navigate(`/app/admin/accounting/journal/${bean.id}`);
            }
            if (suggestion && !suggestionAppliedRef.current && bean.id) {
                suggestionAppliedRef.current = true;
                setLineRows(
                    (suggestion.lines ?? []).map((l, i) => ({
                        key: `sug-${i}`,
                        account: {id: l.accountId, code: l.accountCode, name: l.accountName} as any,
                        debit: l.side === "DEBIT" ? "0" : "",
                        credit: l.side === "CREDIT" ? "0" : "",
                        description: suggestion.label ?? "",
                    }))
                );
            }
        },
        defaultAction: {
            label: "Guardar",
            action: () => form.save(),
            disabled: () => {
                const s = form.propModel.value?.status;
                return s === "CONFIRMADO" || s === "ANULADO";
            },
        },
        removeAction: {
            label: "Borrar",
            color: "error",
            action: () => form.remove(),
            disabled: () => {
                const s = form.propModel.value?.status;
                return s === "CONFIRMADO" || s === "ANULADO";
            },
        },
        extraActions: [
            {
                // Guarda lo pendiente (si el asiento aún se puede editar) y deja el formulario
                // listo para el siguiente, sin volver al listado.
                label: "Nuevo asiento",
                color: "primary",
                action: async () => {
                    const s = form.propModel.value?.status;
                    if (s !== "CONFIRMADO" && s !== "ANULADO") {
                        try {
                            await form.save();
                        } catch {
                            return;
                        }
                    }
                    setLineRows([]);
                    loadedBeanIdRef.current = undefined;
                    navigate("/app/admin/accounting/journal/new");
                    form.loadForced({
                        entryDate: new Date().toISOString().split("T")[0],
                        description: "",
                        origin: "MANUAL",
                        lines: [],
                    } as unknown as JournalEntry);
                },
            } as FormButtonProps,
            {
                label: "Confirmar",
                color: "success",
                disabled: () => {
                    const e = form.propModel.value;
                    return !e?.id || e?.status !== "BORRADOR";
                },
                action: async () => {
                    if (lineRows.length === 0) {
                        apiManager.enqueueSnackbar(
                            "El asiento no tiene líneas.",
                            {variant: "error"}
                        );
                        return;
                    }
                    const totalDebit = lineRows.reduce((s, r) => s + (parseFloat(r.debit) || 0), 0);
                    const totalCredit = lineRows.reduce((s, r) => s + (parseFloat(r.credit) || 0), 0);
                    if (Math.abs(totalDebit - totalCredit) >= 0.001) {
                        apiManager.enqueueSnackbar(
                            `El asiento no está cuadrado. Diferencia: ${fmtAmt(Math.abs(totalDebit - totalCredit))} €`,
                            {variant: "error"}
                        );
                        return;
                    }
                    const entryId = form.getIdOrErr();
                    try {
                        await saveAllLines(entryId);
                    } catch {
                        return;
                    }
                    apiManager.execute(ctrl.confirmJournalEntry(entryId), "Asiento confirmado")
                        .then(j => form.setter(j));
                },
            } as FormButtonProps,
            {
                label: "Reabrir",
                color: "warning",
                disabled: () => form.propModel.value?.status !== "CONFIRMADO",
                action: () => {
                    apiManager.execute(
                        axiosInstance.post<JournalEntry>(`${apiManager.basePath}/api/admin/accounting/journal/${form.getIdOrErr()}/reopen`),
                        "Asiento reabierto"
                    ).then(j => form.setter(j));
                },
            } as FormButtonProps,
            {
                label: "Anular",
                color: "error",
                disabled: () => {
                    const s = form.propModel.value?.status;
                    return !form.propModel.value?.id || s === "ANULADO";
                },
                action: () => {
                    apiManager.execute(ctrl.cancelJournalEntry(form.getIdOrErr()), "Asiento anulado")
                        .then(j => form.setter(j));
                },
            } as FormButtonProps,
            {
                label: "Duplicar",
                color: "secondary",
                disabled: () => !form.propModel.value?.id,
                action: () => {
                    apiManager.execute(ctrl.duplicateJournalEntry(form.getIdOrErr()), "Asiento duplicado")
                        .then((j: any) => {
                            setLineRows([]);
                            loadedBeanIdRef.current = undefined;
                            navigate(`/app/admin/accounting/journal/${j.id}`);
                            form.loadForced(ctrl.getJournalEntry(j.id));
                        });
                },
            } as FormButtonProps,
        ],
    } as UseFormProps<JournalEntry>);

    const bean = form.propModel.value;
    const isConfirmed = bean?.status === "CONFIRMADO" || bean?.status === "ANULADO";

    // Sync lineRows from server when the loaded entry changes.
    useEffect(() => {
        if (bean?.id !== loadedBeanIdRef.current) {
            loadedBeanIdRef.current = bean?.id;
            const serverLines = (bean?.lines as JournalLine[]) ?? [];
            if (serverLines.length > 0) {
                setLineRows(serverLines.map(toLineRow));
            }
        }
    }, [bean?.id]);

    const addLine = () =>
        setLineRows(prev => [...prev, {
            key: String(Date.now() + Math.random()),
            account: null,
            debit: "",
            credit: "",
            description: bean?.description ?? "",
        }]);

    const changeLine = (key: string, changes: Partial<LineRow>) =>
        setLineRows(prev => prev.map(r => r.key === key ? {...r, ...changes} : r));

    const deleteLine = (key: string) => {
        const row = lineRows.find(r => r.key === key);
        if (!row) return;
        const remove = () => setLineRows(prev => prev.filter(r => r.key !== key));
        if (row.id) {
            apiManager.execute(ctrl.deleteLineJournalEntry(row.id)).then(remove);
        } else {
            remove();
        }
    };

    const totalDebit = lineRows.reduce((s, r) => s + (parseFloat(r.debit) || 0), 0);
    const totalCredit = lineRows.reduce((s, r) => s + (parseFloat(r.credit) || 0), 0);
    const isBalanced = lineRows.length > 0 && Math.abs(totalDebit - totalCredit) < 0.001;

    return (
        <FormContent form={form} title="Asiento contable">
            <Grid container spacing={3}>
                <Grid item sm={2}>
                    <FormInput field="entryDate" label="Fecha" type="date"/>
                </Grid>
                {bean?.entryNumber != null && (
                    <Grid item sm={2}>
                        <FormInput field="entryNumber" label="Nº asiento" disabled/>
                    </Grid>
                )}
                <Grid item sm={bean?.entryNumber != null ? 4 : 6}>
                    <FormInput field="description" label="Descripción"/>
                </Grid>
                <Grid item sm={2}>
                    <FormSelect field="origin" label="Origen" type={types.journalOrigins} disabled={isConfirmed}/>
                </Grid>
                <Grid item sm={2} sx={{display: "flex", alignItems: "center", pt: {sm: 4}}}>
                    <SoftChip tone={statusTone[bean?.status ?? "BORRADOR"] ?? "neutral"}
                              label={statusLabel[bean?.status ?? "BORRADOR"] ?? bean?.status}/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item sm={3}>
                    <FormAutocomplete field="period" label="Período contable" type={types.accountingPeriods}/>
                </Grid>
            </Grid>

            {suggestion && (
                <Alert severity="info" sx={{mt: 1, mb: 1}}>
                    Patrón frecuente ({suggestion.occurrences} veces): <strong>{suggestion.label}</strong>
                    {suggestion.lines && suggestion.lines.length > 0 && (
                        <Box component="span" sx={{ml: 1}}>
                            — {suggestion.lines.map(l => `${l.accountCode} ${l.side === "DEBIT" ? "D" : "H"}`).join(" / ")}
                        </Box>
                    )}
                </Alert>
            )}

            {form.getId() && (
                <Box mt={3} sx={{p: {xs: 2, sm: 2.5}, borderRadius: 3, border: "1px solid", borderColor: "divider"}}>
                    <Stack direction={{xs: "column", sm: "row"}} spacing={1.5} justifyContent="space-between"
                           alignItems={{sm: "center"}} mb={2}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Typography variant="subtitle1" fontWeight={700}>Líneas del asiento</Typography>
                            {lineRows.length > 0 && (isBalanced
                                ? <SoftChip tone="success" label="Cuadrado"/>
                                : <SoftChip tone="warning" label={`Descuadre de ${fmtAmt(Math.abs(totalDebit - totalCredit))} €`}/>)}
                        </Stack>
                        {!isConfirmed && (
                            <Button startIcon={<AddRounded/>} variant="outlined" size="small" onClick={addLine}
                                    sx={{alignSelf: {xs: "flex-start", sm: "auto"}}}>
                                Añadir línea
                            </Button>
                        )}
                    </Stack>

                    {/* En móvil la tabla se desplaza dentro de su caja: cuenta, debe y haber se leen en fila. */}
                    <TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
                    <Table size="small" sx={{...tableSx, tableLayout: "fixed", width: "100%", minWidth: 680}}>
                        <TableHead>
                            <TableRow>
                                <TableCell width="35%">Cuenta</TableCell>
                                <TableCell>Descripción</TableCell>
                                <TableCell align="right" width={120}>Debe</TableCell>
                                <TableCell align="right" width={120}>Haber</TableCell>
                                <TableCell width={50}/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lineRows.map(row =>
                                isConfirmed ? (
                                    <TableRow key={row.key}>
                                        <TableCell>
                                            <Box component="span" sx={{fontFamily: "monospace", mr: 1}}>{fmtAccountCode(row.account?.code)}</Box>
                                            <Box component="span" sx={{color: "text.secondary"}}>{(row.account as any)?.name}</Box>
                                        </TableCell>
                                        <TableCell>{row.description}</TableCell>
                                        <TableCell align="right" sx={{fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"}}>{row.debit ? fmtAmt(parseFloat(row.debit)) : ""}</TableCell>
                                        <TableCell align="right" sx={{fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"}}>{row.credit ? fmtAmt(parseFloat(row.credit)) : ""}</TableCell>
                                        <TableCell/>
                                    </TableRow>
                                ) : (
                                    <LineEditor
                                        key={row.key}
                                        row={row}
                                        disabled={isConfirmed}
                                        onChange={changes => changeLine(row.key, changes)}
                                        onDelete={() => deleteLine(row.key)}
                                    />
                                )
                            )}

                            {lineRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{color: "text.secondary", textAlign: "center", py: 4}}>
                                        Sin líneas: pulsa «Añadir línea»
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                        <TableFooter sx={{"& td": {fontWeight: 700, color: "text.primary", fontSize: 14, bgcolor: "grey.50"}}}>
                            <TableRow>
                                <TableCell colSpan={2}>Total</TableCell>
                                <TableCell align="right" sx={{fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"}}>{fmtAmt(totalDebit)}</TableCell>
                                <TableCell align="right" sx={{fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"}}>{fmtAmt(totalCredit)}</TableCell>
                                <TableCell/>
                            </TableRow>
                        </TableFooter>
                    </Table>
                    </TableContainer>

                    {lineRows.length > 0 && !isBalanced && (
                        <Alert severity="warning" sx={{mt: 2, borderRadius: 2}}>
                            No cuadra: la diferencia entre debe y haber es de {fmtAmt(Math.abs(totalDebit - totalCredit))} €.
                        </Alert>
                    )}
                </Box>
            )}
        </FormContent>
    );
};
